import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type SchoolRole = 'teacher' | 'school_leader' | 'system_admin';

export interface Person {
  id: string;
  name: string;
  email: string;
  role: SchoolRole;
  isOwner: boolean;
  joinedAt: string;
  lessons: number;
  lastActiveAt: string | null;
  /** When anyone at the school last nudged them; absent on older APIs. */
  lastNudgedAt?: string | null;
}

export interface Invite {
  id: string;
  email: string;
  role: 'teacher' | 'school_leader';
  createdAt: string;
  expiresAt: string;
  expired: boolean;
  invitedBy: string | null;
  /** An owner invite from EducAitor (admin-created organisation). */
  makeOwner?: boolean;
}

export interface Seats {
  limit: number | null;
  members: number;
  pendingInvites: number;
  used: number;
  available: number | null;
}

/** Joined with the school code and waiting for a leader to approve them. */
export interface JoinRequest {
  id: string;
  name: string;
  email: string;
  requestedAt: string;
}

/** Removed from the school (their account is paused); a leader can add them back. */
export interface RemovedPerson {
  id: string;
  name: string;
  email: string;
  /** Null for people removed before this was recorded. */
  removedAt: string | null;
}

export interface People {
  seats: Seats;
  members: Person[];
  invites: Invite[];
  requests?: JoinRequest[];
  removed?: RemovedPerson[];
  /** 'approval': code joins wait for a leader; 'off': email invites only. */
  codeJoins?: 'approval' | 'off';
  me: { id: string; isOwner: boolean };
}

export interface InviteResult {
  invited: string[];
  skipped: { email: string; reason: string }[];
  emailed: number;
}

export function usePeople() {
  return useQuery({ queryKey: ['people'], queryFn: () => api<People>('/schools/mine/people') });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['people'] });
    qc.invalidateQueries({ queryKey: ['members'] });
  };
}

export function useInvite() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (body: { emails: string[]; role: 'teacher' | 'school_leader' }) =>
      api<InviteResult>('/schools/mine/invites', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: done,
  });
}

export function useInviteAction() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'resend' | 'revoke' }) =>
      action === 'resend'
        ? api(`/schools/mine/invites/${id}/resend`, { method: 'POST', body: '{}' })
        : api(`/schools/mine/invites/${id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
}

export function useMemberAction() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, change }: { id: string; change: { role?: 'teacher' | 'school_leader'; isOwner?: boolean } | 'remove' }) =>
      change === 'remove'
        ? api(`/schools/mine/members/${id}`, { method: 'DELETE' })
        : api(`/schools/mine/members/${id}`, { method: 'PATCH', body: JSON.stringify(change) }),
    onSuccess: done,
  });
}

/** Split pasted text (commas, spaces, new lines, "Name <email>") into addresses. */
export function parseEmails(text: string): string[] {
  return [...new Set((text.match(/[^\s,;<>"']+@[^\s,;<>"']+/g) ?? []).map((e) => e.toLowerCase()))];
}

export function useRequestAction() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'decline' }) =>
      api(`/schools/mine/requests/${id}/${action}`, { method: 'POST', body: '{}' }),
    onSuccess: done,
  });
}

/** Someone on the Removed list: add them back (takes a seat), or clear them off the list. */
export function useRemovedAction() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'restore' | 'clear' }) => api(`/schools/mine/removed/${id}/${action}`, { method: 'POST', body: '{}' }),
    onSuccess: done,
  });
}

/** Nudge staff to record a lesson: some people, or everyone quiet for two weeks. At most one a week each. */
export function useNudge() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (body: { who: string[] | 'quiet'; note?: string }) =>
      api<{ sent: number; skipped: { id: string; name: string }[] }>('/schools/mine/nudge', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: done,
  });
}

export function useCodeJoins() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (mode: 'approval' | 'off') => api('/schools/mine/code-joins', { method: 'PUT', body: JSON.stringify({ mode }) }),
    onSuccess: done,
  });
}
